import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Buildings, ChartLineUp, Eye, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-emerald-500 text-black" },
  { value: "contacted", label: "Contacted", color: "bg-blue-500 text-white" },
  { value: "demo", label: "Demo'd", color: "bg-purple-500 text-white" },
  { value: "signed", label: "Signed", color: "bg-amber-400 text-black" },
  { value: "dead", label: "Dead", color: "bg-zinc-700 text-zinc-300" },
];

export default function CorporateLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/corporate/leads`),
        axios.get(`${API}/corporate/stats`),
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Failed to load corporate leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLead = async (id, fields) => {
    try {
      await axios.patch(`${API}/corporate/leads/${id}`, fields);
      toast.success("Updated");
      load();
      if (selected && selected.id === id) {
        setSelected({ ...selected, ...fields });
      }
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const openLead = (lead) => {
    setSelected(lead);
    setNotes(lead.notes || "");
  };

  const statusBadge = (status) => {
    const s = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
    return <Badge className={`${s.color} font-semibold`}>{s.label}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="corporate-leads-page">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3">
            <Buildings weight="duotone" className="w-8 h-8 text-emerald-500" />
            Corporate Leads
          </h1>
          <p className="text-zinc-400 mt-1">Inquiries from /for-corporations and referral attribution.</p>
        </div>

        {/* Stats row */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-6">
                <p className="text-sm text-zinc-400">Total page visits</p>
                <p className="text-3xl font-black text-emerald-500 mt-2" data-testid="stat-visits">{stats.total_visits}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-6">
                <p className="text-sm text-zinc-400">Total inquiries</p>
                <p className="text-3xl font-black text-emerald-500 mt-2" data-testid="stat-inquiries">{stats.total_inquiries}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-6">
                <p className="text-sm text-zinc-400">Conversion rate</p>
                <p className="text-3xl font-black text-emerald-500 mt-2" data-testid="stat-conversion">{stats.conversion_rate}%</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Referrer breakdown */}
        {stats && stats.by_referrer && stats.by_referrer.length > 0 && (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ChartLineUp className="w-5 h-5 text-emerald-500" /> Attribution by referrer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Referrer</TableHead>
                    <TableHead className="text-zinc-400">Code</TableHead>
                    <TableHead className="text-zinc-400 text-right">Visits</TableHead>
                    <TableHead className="text-zinc-400 text-right">Inquiries</TableHead>
                    <TableHead className="text-zinc-400 text-right">Conversion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.by_referrer.map((row) => (
                    <TableRow key={row.ref_code} className="border-zinc-800">
                      <TableCell className="text-white font-semibold">{row.label}</TableCell>
                      <TableCell className="text-zinc-500 font-mono text-xs">{row.ref_code}</TableCell>
                      <TableCell className="text-zinc-300 text-right">{row.visits}</TableCell>
                      <TableCell className="text-emerald-500 text-right font-bold">{row.inquiries}</TableCell>
                      <TableCell className="text-zinc-300 text-right">
                        {row.visits > 0 ? `${((row.inquiries / row.visits) * 100).toFixed(1)}%` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Leads table */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">All inquiries ({leads.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-zinc-500">Loading…</p>
            ) : leads.length === 0 ? (
              <p className="text-zinc-500 text-center py-12" data-testid="leads-empty">
                No inquiries yet. Once Wardell shares the link and someone submits, they'll appear here.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400">Company</TableHead>
                    <TableHead className="text-zinc-400">Contact</TableHead>
                    <TableHead className="text-zinc-400">Tier</TableHead>
                    <TableHead className="text-zinc-400">Referrer</TableHead>
                    <TableHead className="text-zinc-400">Submitted</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} className="border-zinc-800 hover:bg-zinc-900/50">
                      <TableCell>{statusBadge(l.status)}</TableCell>
                      <TableCell className="text-white font-semibold">{l.company}</TableCell>
                      <TableCell className="text-zinc-300">
                        {l.contact_name}
                        {l.title && <div className="text-xs text-zinc-500">{l.title}</div>}
                      </TableCell>
                      <TableCell className="text-zinc-400 capitalize">{l.tier_interest || "—"}</TableCell>
                      <TableCell className="text-emerald-400 text-sm">{l.ref_code || "—"}</TableCell>
                      <TableCell className="text-zinc-500 text-xs">
                        {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openLead(l)}
                          className="border-zinc-700 hover:border-emerald-500"
                          data-testid={`view-lead-${l.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">{selected.company}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  {statusBadge(selected.status)}
                  {selected.ref_code && <Badge variant="outline" className="border-emerald-700 text-emerald-400">via {selected.ref_code}</Badge>}
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Detail label="Contact" value={selected.contact_name} />
                  <Detail label="Title" value={selected.title || "—"} />
                  <Detail
                    label="Email"
                    value={
                      <a href={`mailto:${selected.email}`} className="text-emerald-500 hover:text-emerald-400 inline-flex items-center gap-1">
                        <EnvelopeSimple className="w-4 h-4" /> {selected.email}
                      </a>
                    }
                  />
                  <Detail
                    label="Phone"
                    value={selected.phone ? (
                      <a href={`tel:${selected.phone}`} className="text-emerald-500 hover:text-emerald-400 inline-flex items-center gap-1">
                        <Phone className="w-4 h-4" /> {selected.phone}
                      </a>
                    ) : "—"}
                  />
                  <Detail label="Tier interest" value={selected.tier_interest || "—"} />
                  <Detail label="Annual budget" value={selected.annual_budget || "—"} />
                </div>

                <Detail
                  label="Focus areas"
                  value={selected.focus_areas && selected.focus_areas.length > 0
                    ? selected.focus_areas.join(", ")
                    : "—"}
                />

                {selected.message && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Message</p>
                    <p className="text-zinc-300 text-sm whitespace-pre-wrap p-3 bg-black border border-zinc-800 rounded">{selected.message}</p>
                  </div>
                )}

                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Update status</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((s) => (
                      <Button
                        key={s.value}
                        size="sm"
                        variant={selected.status === s.value ? "default" : "outline"}
                        onClick={() => updateLead(selected.id, { status: s.value })}
                        className={selected.status === s.value ? s.color : "border-zinc-700"}
                        data-testid={`status-btn-${s.value}`}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Internal notes</p>
                  <Textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="bg-black border-zinc-800"
                    placeholder="Call notes, next steps, decision-maker info…"
                    data-testid="lead-notes-input"
                  />
                  <Button
                    size="sm"
                    onClick={() => updateLead(selected.id, { notes })}
                    className="mt-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                    data-testid="save-notes-btn"
                  >
                    Save notes
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">{label}</p>
    <div className="text-zinc-200">{value}</div>
  </div>
);
